import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  FormatCurrencyForDisplay
} from "@unified-checkout/design-system";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { useIntegrationsStore } from "@app/dashboard/store/integrations";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useFiltersStore } from "@app/dashboard/store/home/filters";

const Currencies = () => {
  const { t } = useTranslation();
  const {
    currencies,
    isError: isIntegrationsError,
    isLoading: isIntegrationsLoading
  } = useIntegrationsStore();
  const { isLive } = useTestLiveModeStore();
  const currentMode = isLive ? "live" : "test";

  const { filters, setFilters } = useFiltersStore();

  const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
  const countryCurrency = COUNTRIES_INFO[country]?.currency;
  const availableCurrencies = currencies?.[currentMode] || [];

  const localizeCurrency = (currency?: string) => (
    (
      currency &&
      !t(`innerLayout.currencies.${currency}`).includes('innerLayout')
    ) ?
      t(`innerLayout.currencies.${currency}`)
      :
      currency
  );

  const getDefaultCurrency = () => {
    if (!availableCurrencies.length) {
      return countryCurrency;
    }

    const currency = availableCurrencies.includes(countryCurrency)
      ? countryCurrency
      : availableCurrencies[0];

    return currency;
  };

  const [selectedCurrency, setSelectedCurrency] = useState(getDefaultCurrency());

  useEffect(() => {
    const currency = !(isIntegrationsError || isIntegrationsLoading)
      ? getDefaultCurrency()
      : filters.currency || countryCurrency;

    setSelectedCurrency(currency as string);

    setFilters({
      currency: currency
    });
  }, [currencies, isLive]);

  const handleClick = (currency: string) => {
    setSelectedCurrency(currency);

    // Set global state with the selected currency
    setFilters({
      currency: currency
    });
  };

  const localizedDefaultCurrency = localizeCurrency(getDefaultCurrency());
  const localizedSelectedCurrency = localizeCurrency(selectedCurrency);

  const selected_currency = currencies?.[currentMode].includes(selectedCurrency || "") ?
    localizedSelectedCurrency
    :
    localizedDefaultCurrency;

  const dropdownBtnText = FormatCurrencyForDisplay(selected_currency) as any;

  return (
    <Dropdown width="fit-content">
      <DropdownButton
        text={dropdownBtnText}
        placeholder={t(`innerLayout.homePage.overview.currencies.selectCurrency`)}
        disabled={!currencies || isIntegrationsLoading || !availableCurrencies.length}
      />
      <DropdownMenu
        width="fit-content"
        horizontalPlacement="end"
      >
        {
          currencies?.[currentMode].map((currency, i: number) => {
            const localizedCurrency = localizeCurrency(currency);
            const menuItemText = FormatCurrencyForDisplay(localizedCurrency) as any;

            return <DropdownMenuItem
              key={`currency-${i}-${currency}`}
              text={menuItemText}
              selected={selectedCurrency === currency}
              onClickHandler={() => handleClick(currency)}
            />
          })
        }
      </DropdownMenu>

    </Dropdown>
  )
}

export default Currencies;